import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles, StatusChip, DocumentBrandHeader, PoweredByDuoNep } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { formatDate, formatDateTime } from '@utils/dateUtils'

// Helper function to get initials from a name
const getInitials = (name) => {
  if (!name) return ''
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .join('')
}

const WorkOrderPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [workOrder, setWorkOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  useEffect(() => {
    const fetchWorkOrder = async () => {
      try {
        const response = await api.get(`/maintenance/work-orders/${id}/`)
        setWorkOrder(response.data)
      } catch (error) {
        console.error('Error fetching work order:', error)
        toast.error('Failed to load work order')
        navigate('/work-orders')
      } finally {
        setLoading(false)
      }
    }
    fetchWorkOrder()
  }, [id, navigate])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    if (workOrder) {
      document.title = `Work Order - ${workOrder.work_order_number} - DuoNep`
    }
    return () => {
      document.title = 'DuoNep'
    }
  }, [workOrder])

  // Register document when work order is loaded
  useEffect(() => {
    if (workOrder && !verificationCode) {
      registerDocument('work_order', workOrder.work_order_number, parseInt(id), workOrder)
        .catch((err) => console.error('Failed to register document:', err))
    }
  }, [workOrder, id, verificationCode, registerDocument])

  const handlePrint = async () => {
    // Ensure document is registered before printing
    if (!verificationCode && workOrder) {
      try {
        await registerDocument('work_order', workOrder.work_order_number, parseInt(id), workOrder)
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!workOrder) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate(`/work-orders/${id}`)}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Co-branded document header */}
        <DocumentBrandHeader title="WORK ORDER" />

        {/* Work Order Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1">
            <strong>Work Order #:</strong> {workOrder.work_order_number}
          </Typography>
          <Typography variant="body1">
            <strong>Status:</strong> {workOrder.status_display || workOrder.status}
          </Typography>
          <Typography variant="body1">
            <strong>Date:</strong> {formatDate(workOrder.created_at)}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Work Order Details */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          WORK ORDER DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Title</TableCell>
                <TableCell colSpan={3}>{workOrder.title || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell>{workOrder.category_display || workOrder.category || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell>{workOrder.priority_display || workOrder.priority || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                <TableCell>{workOrder.due_date ? formatDate(workOrder.due_date) : '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Requested By</TableCell>
                <TableCell>{workOrder.requested_by_name || workOrder.created_by_name || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Asset Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          EQUIPMENT INFORMATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Name</TableCell>
                <TableCell sx={{ width: '25%' }}>{workOrder.asset_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Tag</TableCell>
                <TableCell sx={{ width: '25%' }}>{workOrder.asset_tag || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                <TableCell>{workOrder.asset_location || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Section/Unit</TableCell>
                <TableCell>{workOrder.department_name || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Assignment Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          ASSIGNMENT DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Assigned To</TableCell>
                <TableCell sx={{ width: '25%' }}>{workOrder.assigned_to_name || 'Unassigned'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Estimated Hours</TableCell>
                <TableCell sx={{ width: '25%' }}>{workOrder.estimated_hours || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Time</TableCell>
                <TableCell>{workOrder.start_time ? formatDateTime(workOrder.start_time) : '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Completion Time</TableCell>
                <TableCell>{workOrder.completion_time ? formatDateTime(workOrder.completion_time) : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Actual Hours</TableCell>
                <TableCell>{workOrder.actual_hours || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Downtime</TableCell>
                <TableCell>{workOrder.downtime_hours ? `${workOrder.downtime_hours} hrs` : '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Problem Description */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          PROBLEM DESCRIPTION
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {workOrder.description || 'No description provided'}
          </Typography>
        </Paper>

        {/* Resolution */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          RESOLUTION / WORK PERFORMED
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {workOrder.resolution_description || 'No resolution description provided'}
          </Typography>
        </Paper>

        {/* Parts Used */}
        {workOrder.parts_used && workOrder.parts_used.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              PARTS / MATERIALS USED
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Unit Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workOrder.parts_used.map((part, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{part.spare_part_name || part.part_name}</TableCell>
                      <TableCell>{part.part_number || '-'}</TableCell>
                      <TableCell align="center">{part.quantity_used || part.quantity}</TableCell>
                      <TableCell align="right">{part.unit_cost ? `UGX ${Number(part.unit_cost).toLocaleString()}` : '-'}</TableCell>
                      <TableCell align="right">{part.total_cost ? `UGX ${Number(part.total_cost).toLocaleString()}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Labor Records */}
        {workOrder.labor_records && workOrder.labor_records.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              LABOR RECORDS
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Technician</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Hours</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workOrder.labor_records.map((labor, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{labor.technician_name}</TableCell>
                      <TableCell>{formatDate(labor.work_date)}</TableCell>
                      <TableCell align="center">{labor.hours_worked}</TableCell>
                      <TableCell>{labor.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Signatures Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          SIGNATURES
        </Typography>

        {/* Technician/Engineer Signature */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2"><strong>Technician/Engineer:</strong></Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Sign:</Typography>
            <Box sx={{ borderBottom: '1px solid black', minWidth: 80, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>{getInitials(workOrder.assigned_to_name)}</Typography>
            </Box>
          </Box>
          <Typography variant="body2">Name: {workOrder.assigned_to_name || '_________'}</Typography>
          <Typography variant="body2">Date: {workOrder.completion_time ? formatDate(workOrder.completion_time) : '_________'}</Typography>
        </Box>

        {/* Reported By / Requester */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
          <Typography variant="body2"><strong>Reported By:</strong></Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Sign:</Typography>
            <Box sx={{ borderBottom: '1px solid black', minWidth: 80, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>{getInitials(workOrder.reported_by_name || workOrder.created_by_name)}</Typography>
            </Box>
          </Box>
          <Typography variant="body2">Name: {workOrder.reported_by_name || workOrder.created_by_name || '_________'}</Typography>
          <Typography variant="body2">Date: {workOrder.created_at ? formatDate(workOrder.created_at) : '_________'}</Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="Work Order"
            documentNumber={workOrder?.work_order_number || ''}
            size={80}
          />
        </Box>

        {/* Powered by DuoNep */}
        <PoweredByDuoNep />
      </Box>

      <PrintStyles orientation="portrait" />
    </>
  )
}

export default WorkOrderPrint
